/**
 * Admin Roadmap — moderation + curation surface for the public roadmap.
 *
 * Connects to /api/admin/roadmap/* (backend: routers/admin_roadmap.py).
 *
 * Capabilities:
 *   - List items with status/category/visibility/search filters
 *   - Composition strip showing public feed mix vs. the 80% target
 *   - Create admin-authored items (seeds, direct roadmap entries)
 *   - Edit any field (including status + visibility transitions)
 *   - Soft-delete items
 *   - Promote items between internal_only ↔ public (with 80/20 safety warning)
 *
 * Deferred: promote-from-ticket (requires a ticket picker UI — separate page).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Plus,
  Pin,
  PinOff,
  Trash2,
  RefreshCw,
  X,
  Eye,
  EyeOff,
  Check,
  // Phase 1.5: view toggle icons
  List,
  LayoutGrid,
  // Phase 6 polish v17 (2026-05-12): category-scoped view icons.
  // Sparkles = Features (suggests "new, exciting"); Bug = Bugs.
  Sparkles,
  Bug,
  // Phase 6 polish v19 (2026-05-13): recency-scoped view icon. Clock
  // signals time-window scope (past 24h) without overloading Sparkles
  // (which already means Features).
  Clock,
  // Phase 6 polish v24 (2026-05-13): Moon = TOMORROW (the night-before
  // planning lane). Read as "set this aside for tomorrow morning".
  Moon,
  // Phase 6 polish v38 (2026-06-01): ChevronsUp = NEXT UP (the manual
  // queue between TOMORROW and BACKLOG). Double-up-arrow shape reads
  // as "promote this for later" — replaces the v37 ⏫ emoji per the
  // project's no-emoji-icons rule.
  ChevronsUp,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RowReorderPointerSensor } from './adminRoadmapDragGuard';
import { T, card, sectionLabel, heading, badge, btnPrimary } from '../utils/adminDesignTokens';
import {
  adminRoadmapService,
  type AdminRoadmapItem,
  type AdminRoadmapSourceTicket,
  type AdminCompositionResponse,
  type RoadmapCategory,
  type RoadmapStatus,
  type RoadmapVisibility,
  type RoadmapSeverity,
  STATUS_LABEL,
  CATEGORY_LABEL,
  VISIBILITY_LABEL,
  SEVERITY_LABEL,
  STATUS_COLORS,
  CATEGORY_COLORS,
  VISIBILITY_COLORS,
  SEVERITY_COLORS,
  FEATURE_STRIPE_COLOR,
  SOURCE_LABEL,
  type RoadmapSource,
  type MentionHandle,
} from '../services/adminRoadmapService';
import { RoadmapEditorModal } from '../components/admin/RoadmapEditorModal';
import { RoadmapKanban } from '../components/admin/RoadmapKanban';
import { storage } from '../services/storage';

const PAGE_SIZE = 50;

// Phase 1.6: lifecycle-ordered status options for the page filter.
// Must stay in lockstep with the same constant in RoadmapEditorModal.tsx —
// guarded by tests/gate/test_phase1_status_flow_consistency.py. 'todo'
// is intentionally absent: the enum value persists in the DB for legacy
// rows but is deprecated UI-side after the post-Phase-1.5 flow refinement.
//
// Flow: intake (admin-only) → under_review (user-visible if public) →
// planned → in_progress → stage (deployed to staging) → qa (admin-only)
// → shipped.
//
// Phase 6 polish v16 (2026-05-12): 'stage' added between in_progress
// and qa. Previously this list lagged the inline list dropdown — admins
// could pick 'stage' on a row but not from the page-filter or the editor
// modal. Forensic pass surfaced the gap before users hit it.
const STATUS_VALUES: RoadmapStatus[] = [
  'intake', 'under_review', 'planned', 'in_progress', 'stage', 'qa',
  'shipped', 'declined', 'duplicate',
];
const CATEGORY_VALUES: RoadmapCategory[] = ['feature', 'improvement', 'known_issue'];
const VISIBILITY_VALUES: RoadmapVisibility[] = ['public', 'internal_only', 'hidden'];
// Phase 1 (migration 1361)
const SOURCE_VALUES: ('admin_manual' | 'in_app_form' | 'support_form' | 'slack')[] = [
  'admin_manual', 'in_app_form', 'support_form', 'slack',
];

// ────────────────────────────────────────────────────────────────────

export const AdminRoadmap: React.FC = () => {
  const [items, setItems] = useState<AdminRoadmapItem[]>([]);
  const [total, setTotal] = useState(0);
  const [composition, setComposition] = useState<AdminCompositionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase 1.5: view toggle. Sticky via ?view=kanban URL param so admins
  // can bookmark the layout they prefer.
  // Phase 5: 'completed' view added — shipped items live on a sub-page
  // so the main List doesn't pile up indefinitely.
  // Phase 6 polish v6 (2026-05-11): 'declined' view added — same
  // pattern as completed. Declined items hide from main List + Kanban
  // by default; admin opens the Declined tab to find them.
  // Phase 6 polish v17 (2026-05-12): 'features' + 'bugs' added —
  // category-scoped set views so admins can see every feature or
  // every bug across the full lifecycle without setting a category
  // filter manually. Mirrors the completed/declined pattern but
  // partitions by category instead of status.
  // Phase 6 polish v19 (2026-05-13): 'new' added — items created
  // within the past 24 hours, surfaced across the full lifecycle
  // so admins can triage everything that landed recently without
  // sorting by created_at manually.
  const [view, setView] = useState<'list' | 'kanban' | 'completed' | 'declined' | 'features' | 'bugs' | 'new'>(() => {
    if (typeof window === 'undefined') return 'list';
    const v = new URLSearchParams(window.location.search).get('view');
    if (v === 'kanban') return 'kanban';
    if (v === 'completed') return 'completed';
    if (v === 'declined') return 'declined';
    if (v === 'features') return 'features';
    if (v === 'bugs') return 'bugs';
    if (v === 'new') return 'new';
    return 'list';
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (view === 'kanban') url.searchParams.set('view', 'kanban');
    else if (view === 'completed') url.searchParams.set('view', 'completed');
    else if (view === 'declined') url.searchParams.set('view', 'declined');
    else if (view === 'features') url.searchParams.set('view', 'features');
    else if (view === 'bugs') url.searchParams.set('view', 'bugs');
    else if (view === 'new') url.searchParams.set('view', 'new');
    else url.searchParams.delete('view');
    window.history.replaceState({}, '', url.toString());
  }, [view]);

  const [statusFilter, setStatusFilter] = useState<RoadmapStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<RoadmapCategory | ''>('');
  const [visibilityFilter, setVisibilityFilter] = useState<RoadmapVisibility | ''>('');
  // Phase 1 (migration 1361)
  const [sourceFilter, setSourceFilter] = useState<RoadmapSource | ''>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);

  // Phase 6 polish v16 (2026-05-12): cache the admin handles so the
  // inline Assignee dropdown can list ALL admins (not just hardcoded
  // matt/paul). Fetched once on mount; refresh isn't needed often
  // enough to justify polling. Falls back to FALLBACK_ASSIGNEE_OPTIONS
  // if the fetch fails (e.g., during the very first paint).
  const [mentionHandles, setMentionHandles] = useState<MentionHandle[] | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adminRoadmapService.listMentionHandles();
        if (!cancelled) setMentionHandles(list);
      } catch {
        // Fall back to hardcoded matt/paul on failure
        if (!cancelled) setMentionHandles(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const assigneeOptions = useMemo(
    () => buildAssigneeOptions(mentionHandles),
    [mentionHandles],
  );

  const [editingItem, setEditingItem] = useState<AdminRoadmapItem | null>(null);
  const [editingItemTickets, setEditingItemTickets] = useState<AdminRoadmapSourceTicket[]>([]);
  const [creating, setCreating] = useState(false);

  // When admin clicks a row to edit, fetch the detail so we have the
  // linked support tickets to show in the modal. The list endpoint only
  // returns summaries (no source_tickets array).
  //
  // Phase 6 polish v27 (2026-05-13): push `?item=<id>` to the URL bar
  // when the modal opens so admins can copy the address (or copy via
  // the modal's Copy link button — same result). On close the param
  // clears. Refresh-while-open re-hydrates the modal from the URL,
  // matching the Slack-DM deep-link UX.
  const openEdit = useCallback(async (item: AdminRoadmapItem) => {
    setEditingItem(item);
    setEditingItemTickets([]);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('item', item.id);
      window.history.replaceState({}, '', url.toString());
    }
    if (item.source_ticket_count > 0) {
      try {
        const detail = await adminRoadmapService.detail(item.id);
        setEditingItemTickets(detail.source_tickets ?? []);
      } catch (e) {
        // Non-fatal — modal just shows no linked tickets
      }
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, categoryFilter, visibilityFilter, sourceFilter, debouncedSearch]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Phase 6 polish v6 (2026-05-11): three special views set their
      // status filter + include_* flag together. Main list keeps
      // shipped + declined hidden via the backend defaults.
      //
      // Phase 6 polish v17 (2026-05-12): 'features' + 'bugs' partition
      // by CATEGORY (not status) and surface the full lifecycle —
      // include shipped + declined so admins see the complete history
      // of every feature / bug. The status filter dropdown still
      // works to narrow further within a category view.
      // Phase 6 polish v19 (2026-05-13): 'new' partitions by RECENCY
      // (created_within_hours=24) and surfaces the full lifecycle so
      // admins can triage everything that landed in the last day.
      const viewStatus =
        view === 'completed' ? 'shipped'
        : view === 'declined' ? 'declined'
        : (statusFilter || undefined);
      const viewCategory: RoadmapCategory | undefined =
        view === 'features' ? 'feature'
        : view === 'bugs' ? 'known_issue'
        : (categoryFilter || undefined);
      // Category-scoped views include everything across the lifecycle
      // so the "set view of all features/bugs" includes shipped + declined.
      const isCategoryView = view === 'features' || view === 'bugs';
      const isNewView = view === 'new';
      const data = await adminRoadmapService.list({
        status: viewStatus,
        category: viewCategory,
        visibility: visibilityFilter || undefined,
        source: sourceFilter || undefined,
        search: debouncedSearch || undefined,
        // Surface the full lifecycle on category- and recency-scoped
        // views — those are "see everything matching X" surfaces, not
        // a filtered backlog. The defaults (hide shipped + declined)
        // still apply to the main List view.
        include_shipped: view === 'completed' || isCategoryView || isNewView,
        include_declined: view === 'declined' || isCategoryView || isNewView,
        created_within_hours: isNewView ? 24 : undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load roadmap items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, visibilityFilter, sourceFilter, debouncedSearch, offset, view]);

  // Phase 6 polish v16 (2026-05-12): the CompositionStrip is hidden
  // (see comment near the render site), so fetching /composition on
  // every mount + every mutation is wasted work. Gate the network
  // call behind a flag so re-enabling the strip is a one-line flip.
  // setComposition is kept current so the React useState shape
  // doesn't drift. Forensic-pass finding #7.
  const COMPOSITION_STRIP_ENABLED = false;
  const loadComposition = useCallback(async () => {
    if (!COMPOSITION_STRIP_ENABLED) return;
    try {
      setComposition(await adminRoadmapService.composition());
    } catch (e) {
      console.error('Composition load failed', e);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Phase 7: deep-link support. The mention DM contains
  // /admin/roadmap?item=<id>. On mount, if that param is present, open
  // the editor modal on that item once items have loaded.
  //
  // Phase 6 polish v27 (2026-05-13): we DON'T clear the param after
  // opening anymore. v27 makes ?item the canonical "modal is open on X"
  // state — `openEdit` writes the param, the modal-close handler
  // clears it. Keeping the param on hydration means refresh-while-open
  // reopens the same modal (which is what admins expect from a
  // copyable URL).
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const itemId = new URLSearchParams(window.location.search).get('item');
    if (!itemId || items.length === 0) return;
    // Only auto-open if no modal is already open (avoid clobbering
    // the admin's in-progress edit when items reload).
    if (editingItem) return;
    const target = items.find((i) => i.id === itemId);
    if (target) {
      openEdit(target);
    }
  }, [items, openEdit, editingItem]);

  useEffect(() => {
    loadComposition();
  }, [loadComposition]);

  const handleDelete = useCallback(
    async (item: AdminRoadmapItem) => {
      if (!window.confirm(`Delete "${item.title}"? This is a soft delete — admin can still recover.`)) {
        return;
      }
      try {
        await adminRoadmapService.remove(item.id);
        await Promise.all([loadItems(), loadComposition()]);
      } catch (e: any) {
        alert(`Delete failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems, loadComposition],
  );

  const handleTogglePin = useCallback(
    async (item: AdminRoadmapItem) => {
      try {
        await adminRoadmapService.patch(item.id, { pinned: !item.pinned });
        await loadItems();
      } catch (e: any) {
        alert(`Pin toggle failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );

  // Migration 1731 — persist a hand-dragged order for one List lane
  // (scope='list') or Kanban column (scope='kanban'). Optimistically writes
  // evenly-spaced ranks onto the dragged rows so the UI settles instantly;
  // on failure it reverts to server truth. The order is meaningful only
  // WITHIN the partition, so lane/column ranks never interfere with each other.
  const handleReorder = useCallback(
    async (scope: 'list' | 'kanban', orderedIds: string[]) => {
      const rankField = scope === 'list' ? 'list_manual_rank' : 'kanban_manual_rank';
      const rankById = new Map(orderedIds.map((id, i) => [id, i]));
      setItems((prev) =>
        prev.map((it) =>
          rankById.has(it.id) ? { ...it, [rankField]: rankById.get(it.id)! } : it,
        ),
      );
      try {
        await adminRoadmapService.reorder(scope, orderedIds);
      } catch (e: any) {
        alert(`Reorder failed: ${e?.message ?? 'unknown'}`);
        await loadItems(); // revert to server order
      }
    },
    [loadItems],
  );

  // Phase 6 polish v27 (2026-05-13): centralised close handler so the
  // URL ?item= param is cleared exactly once, regardless of whether
  // the modal closes via Save, Cancel, or the X button. Without this,
  // a stale ?item= would linger in the address bar after close —
  // confusing if the admin then bookmarks the page expecting the list
  // view, not the just-closed item.
  const closeEditor = useCallback(() => {
    setEditingItem(null);
    setEditingItemTickets([]);
    setCreating(false);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('item')) {
        url.searchParams.delete('item');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, []);

  const handleSaved = useCallback(async () => {
    closeEditor();
    await Promise.all([loadItems(), loadComposition()]);
  }, [closeEditor, loadItems, loadComposition]);

  // Phase 5: inline-PATCH from List view. Status dropdown + TODAY toggle
  // both flow through these helpers — same PATCH endpoint as the editor
  // modal, so server-side validation + history logging stays consistent.
  const handleStatusChange = useCallback(
    async (item: AdminRoadmapItem, newStatus: RoadmapStatus) => {
      try {
        await adminRoadmapService.patch(item.id, { status: newStatus });
        await Promise.all([loadItems(), loadComposition()]);
      } catch (e: any) {
        alert(`Status change failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems, loadComposition],
  );

  // Phase 4 polish (2026-05-11): inline category dropdown on the List
  // view — mirrors the status pattern. Same PATCH path, also reloads
  // the composition meter since category flips can shift the public
  // new/improved/known_issue split.
  const handleCategoryChange = useCallback(
    async (item: AdminRoadmapItem, newCategory: RoadmapCategory) => {
      try {
        await adminRoadmapService.patch(item.id, { category: newCategory });
        await Promise.all([loadItems(), loadComposition()]);
      } catch (e: any) {
        alert(`Category change failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems, loadComposition],
  );

  // Phase 6 polish v12 (2026-05-11): inline assignee dropdown — slack
  // handles of available admins (matt / paul), plus an "unassigned"
  // empty value that clears the column.
  const handleAssigneeChange = useCallback(
    async (item: AdminRoadmapItem, newAssignee: string) => {
      try {
        await adminRoadmapService.patch(item.id, { assignee: newAssignee });
        await loadItems();
      } catch (e: any) {
        alert(`Assignee change failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );

  // Phase 6 polish v30 (2026-05-15): handleNeedsTriageClear removed
  // along with the "?" marker render. The data column still exists
  // on the backend (and the v29 auto-clear still fires server-side
  // when an admin sets category/severity/status) but there is no
  // longer a click affordance because there is no longer a marker.

  // Phase 4 polish v2 (2026-05-11): inline severity + visibility dropdowns.
  // Severity accepts an empty string to clear the column (per the
  // ItemPatch contract — backend interprets '' as "clear severity").
  const handleSeverityChange = useCallback(
    async (item: AdminRoadmapItem, newSeverity: RoadmapSeverity | '') => {
      try {
        await adminRoadmapService.patch(item.id, { severity: newSeverity });
        await loadItems();
      } catch (e: any) {
        alert(`Severity change failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );
  // Visibility flips can shift public composition (e.g. promoting an
  // internal item to public bumps the new/improved %), so we reload
  // both the table and the composition meter — same as category.
  const handleVisibilityChange = useCallback(
    async (item: AdminRoadmapItem, newVisibility: RoadmapVisibility) => {
      try {
        await adminRoadmapService.patch(item.id, { visibility: newVisibility });
        await Promise.all([loadItems(), loadComposition()]);
      } catch (e: any) {
        alert(`Visibility change failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems, loadComposition],
  );

  const handleTodayToggle = useCallback(
    async (item: AdminRoadmapItem) => {
      try {
        await adminRoadmapService.patch(item.id, {
          is_today: !(item.is_today ?? false),
        });
        await loadItems();
      } catch (e: any) {
        alert(`Today toggle failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );

  // Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW toggle.
  // Mutually exclusive with the TODAY toggle — backend clears
  // is_today when admin pins to TOMORROW (and vice versa). The
  // midnight-ET rollover cron promotes everything in TOMORROW into
  // TODAY automatically, so admin's plan-ahead work survives the
  // calendar boundary.
  const handleTomorrowToggle = useCallback(
    async (item: AdminRoadmapItem) => {
      try {
        await adminRoadmapService.patch(item.id, {
          is_tomorrow: !(item.tomorrow_set_at != null),
        });
        await loadItems();
      } catch (e: any) {
        alert(`Tomorrow toggle failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );

  // Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP toggle.
  // Mirror of handleTomorrowToggle. Backend enforces mutual exclusion
  // with is_today + is_tomorrow. UNLIKE TOMORROW, NEXT UP does not
  // auto-promote — admin moves it manually when they're ready.
  const handleNextUpToggle = useCallback(
    async (item: AdminRoadmapItem) => {
      try {
        await adminRoadmapService.patch(item.id, {
          is_next_up: !(item.next_up_set_at != null),
        });
        await loadItems();
      } catch (e: any) {
        alert(`Next-up toggle failed: ${e?.message ?? 'unknown'}`);
      }
    },
    [loadItems],
  );

  // Phase 6 polish v37 (2026-06-01): per-section collapse state.
  // Persists per-browser via the `storage` service (localStorage on
  // web, AsyncStorage on native). Default = all expanded. Storage
  // keys: `roadmap.collapsed.<lane>` → '1' (collapsed) or '0' (open).
  type LaneKey = 'today' | 'tomorrow' | 'next_up' | 'backlog';
  const [collapsed, setCollapsed] = useState<Record<LaneKey, boolean>>({
    today: false, tomorrow: false, next_up: false, backlog: false,
  });
  // Hydrate from storage once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lanes: LaneKey[] = ['today', 'tomorrow', 'next_up', 'backlog'];
      const next: Record<LaneKey, boolean> = {
        today: false, tomorrow: false, next_up: false, backlog: false,
      };
      for (const lane of lanes) {
        try {
          const raw = await storage.getItem(`roadmap.collapsed.${lane}`);
          next[lane] = raw === '1';
        } catch { /* ignore */ }
      }
      if (!cancelled) setCollapsed(next);
    })();
    return () => { cancelled = true; };
  }, []);
  const toggleCollapse = useCallback((lane: LaneKey) => {
    setCollapsed((prev) => {
      const nextVal = !prev[lane];
      // Fire-and-forget persistence — UI updates synchronously.
      storage.setItem(`roadmap.collapsed.${lane}`, nextVal ? '1' : '0').catch(() => {});
      return { ...prev, [lane]: nextVal };
    });
  }, []);

  return (
    <div style={{ padding: '24px 32px', background: '#F9FAFB', minHeight: '100vh' }}>
      {/* ── Page header ───────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20,
      }}>
        <div>
          <h1 style={{ ...heading, fontSize: 28, margin: 0 }}>Roadmap</h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: '4px 0 0' }}>
            Curate the public product roadmap. Items only appear to users when visibility is Public.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { loadItems(); loadComposition(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              background: '#FFFFFF',
              border: `1px solid ${T.limestone}`,
              borderRadius: T.radiusMd,
              fontWeight: 600, fontSize: 13, color: T.textSecondary,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={() => setCreating(true)}
            style={{
              ...btnPrimary,
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', fontSize: 13,
            }}
          >
            <Plus size={14} />
            New Item
          </button>
        </div>
      </div>

      {/* Phase 6 polish v28 (2026-05-13): merged the tab toolbar and
          filter row onto a SINGLE flex row — tabs left, filters
          right (pushed via marginLeft:auto on the filter group). The
          search input + select dropdowns are tighter than before so
          they fit the right edge cleanly on a 1280-1920px window
          without wrapping. Saves ~50px of vertical space and keeps
          everything an admin needs visible in a single glance. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',  // narrow viewports gracefully fall back to two rows
      }}>
      {/* ── View toggle (Phase 1.5) ────────────────────────────
          List view = existing flat list with filters + pagination.
          Kanban view = 6 columns mapped to lifecycle, drag-drop transitions. */}
      <div style={{
        display: 'inline-flex',
        background: '#FFFFFF',
        border: `1px solid ${T.limestone}`,
        borderRadius: T.radiusMd,
        padding: 3,
        gap: 2,
        flexShrink: 0,  // tabs keep their natural width; filters shrink first
      }}>
        {/* Phase 6 polish v25 (2026-05-13): tab reorder. Tabs split
            into two visual groups separated by a vertical divider:
              [View mode]   List · Kanban
              [Scope]       Features · Bugs · New · Completed
            "Declined" tab dropped from the toolbar (items still hide
            from main List by default; status filter dropdown reaches
            them if needed). URL hydration for ?view=declined kept for
            backward compat with old bookmarks. */}
        <button
          onClick={() => setView('list')}
          aria-pressed={view === 'list'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'list' ? T.cedarSubtle : 'transparent',
            color: view === 'list' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <List size={14} />
          List
        </button>
        <button
          onClick={() => setView('kanban')}
          aria-pressed={view === 'kanban'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'kanban' ? T.cedarSubtle : 'transparent',
            color: view === 'kanban' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <LayoutGrid size={14} />
          Kanban
        </button>
        {/* Vertical divider — separates view-mode tabs (List/Kanban)
            from scope tabs (Features/Bugs/New/Completed). Per user spec
            "add a break line after kanban". */}
        <div
          aria-hidden
          style={{
            width: 1,
            alignSelf: 'stretch',
            backgroundColor: T.limestone,
            margin: '4px 6px',
          }}
        />
        {/* Phase 6 polish v17 (2026-05-12): Features sub-view. Partitions
            by CATEGORY (not status) and includes the FULL lifecycle —
            shipped + declined are surfaced so admins see every feature
            we've ever worked on, not just active ones. The status filter
            dropdown still narrows further within the view.
            Phase 6 polish v25 (2026-05-13): now uses the 3-section
            (TODAY/TOMORROW/BACKLOG) layout — admins keep their
            day-planning context inside the category view. */}
        <button
          onClick={() => setView('features')}
          aria-pressed={view === 'features'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'features' ? T.cedarSubtle : 'transparent',
            color: view === 'features' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Sparkles size={14} />
          Features
        </button>
        {/* Phase 6 polish v17 (2026-05-12): Bugs sub-view. category=
            known_issue (which renders as "Bug" in the label map). Same
            full-lifecycle treatment as Features. */}
        <button
          onClick={() => setView('bugs')}
          aria-pressed={view === 'bugs'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'bugs' ? T.cedarSubtle : 'transparent',
            color: view === 'bugs' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Bug size={14} />
          Bugs
        </button>
        {/* Phase 6 polish v19 (2026-05-13): New sub-view. Filters to items
            created within the past 24 hours, surfaced across the FULL
            lifecycle so admins can quickly find everything that landed
            recently. */}
        <button
          onClick={() => setView('new')}
          aria-pressed={view === 'new'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'new' ? T.cedarSubtle : 'transparent',
            color: view === 'new' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
          title="Items created in the past 24 hours"
        >
          <Clock size={14} />
          New
        </button>
        {/* Phase 5: Completed sub-view. Renders only shipped items
            (sorted by shipped_at desc). Phase 6 polish v25: moved to
            the end of the scope group — shipped items are the least
            actively touched, so they sit furthest from the day-to-day
            scope tabs (Features/Bugs/New). Kept as a flat list (no
            TODAY/TOMORROW split) since shipped items don't get planned. */}
        <button
          onClick={() => setView('completed')}
          aria-pressed={view === 'completed'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            border: 'none',
            borderRadius: T.radiusSm,
            background: view === 'completed' ? T.cedarSubtle : 'transparent',
            color: view === 'completed' ? T.cedarPrimary : T.textSecondary,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Check size={14} />
          Completed
        </button>
      </div>

      {/* Phase 6 polish v13 (2026-05-11): CompositionStrip hidden per
          user feedback — "we also do not need these metrics for the
          roadmap, it's clutter for now". State + fetch logic kept in
          place so it can be re-enabled later by uncommenting; the
          /api/admin/roadmap/composition call also still runs (admins
          who care can hit the endpoint directly). */}
      {/* {composition && <CompositionStrip composition={composition} />} */}

      {/* ── Filters (Phase 6 polish v28: same row as tabs) ─────
          Pushed to the right edge via marginLeft:auto so the tabs
          keep their natural width on the left. Tighter sizing than
          the previous full-width card row — search 200px, dropdowns
          ~120px. flexWrap on the parent means narrow viewports still
          fall back to a two-row layout cleanly. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginLeft: 'auto',
        flexShrink: 1,
      }}>
        <input
          type="text"
          placeholder="Search title or body..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: 200,
            padding: '6px 12px',
            border: `1px solid ${T.limestone}`,
            borderRadius: T.radiusMd,
            fontSize: 13,
            outline: 'none',
            color: T.textPrimary,
            fontFamily: T.fontBody,
            background: '#FFFFFF',
          }}
        />
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value as any)}
          style={{ ...selectStyle, minWidth: 0, width: 130 }}
        >
          <option value="">All visibility</option>
          {VISIBILITY_VALUES.map((v) => (
            <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          style={{ ...selectStyle, minWidth: 0, width: 130 }}
        >
          <option value="">All statuses</option>
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
        {/* Phase 6 polish v25 (2026-05-13): "All categories" and "All
            sources" filter dropdowns removed per user feedback. The
            tab navigation (Features / Bugs) already partitions by
            category, and Source-of-Truth filtering had near-zero
            usage post-launch. State + service params kept (categoryFilter,
            sourceFilter, SOURCE_VALUES, CATEGORY_VALUES) so the URL
            contract + loader still works — only the UI controls are
            removed. Future re-enable: just restore the two <select>
            blocks. */}
      </div>
      </div>  {/* close v28 outer single-row toolbar */}

      {/* ── List ──────────────────────────────────────────────── */}
      {error && (
        <div style={{
          ...card,
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          boxShadow: 'none',
          color: '#991B1B',
          marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertTriangle size={16} />
          <span style={{ fontSize: 13 }}>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 20px', color: T.textSecondary }}>
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ fontSize: 14, color: T.textSecondary, margin: '0 0 12px' }}>
            No items match these filters.
          </p>
          <button
            onClick={() => setCreating(true)}
            style={{ ...btnPrimary, padding: '8px 16px', fontSize: 13 }}
          >
            Create your first item
          </button>
        </div>
      ) : view === 'kanban' ? (
        // Phase 1.5 Kanban view. We pass the same `items` array — the
        // Kanban renders only items whose status is in the Kanban
        // lifecycle set (intake/under_review/planned/in_progress/qa/
        // shipped). Items in other statuses (declined, duplicate, the
        // deprecated 'todo') don't appear in Kanban; admins access
        // them via List view's status filter dropdown.
        <RoadmapKanban
          items={items}
          onEdit={openEdit}
          onItemUpdated={() => { loadItems(); loadComposition(); }}
        />
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {/* Phase 5: /completed → flat compact list (no TODAY/BACKLOG).
              Phase 6 polish v25 (2026-05-13): /features, /bugs, /new
              moved BACK to the 3-section (TODAY/TOMORROW/BACKLOG)
              layout — user wanted day-planning context preserved
              inside the scope views ("on these specific pages I would
              like to have it continue to show the today/tomorrow and
              backlog"). Only /completed stays flat: shipped items
              don't get planned, so TODAY/TOMORROW lanes are always
              empty for them. */}
          {view === 'completed' ? (
            <RoadmapTable
              items={items}
              total={total}
              onEdit={openEdit}
              onDelete={handleDelete}
              onTogglePin={handleTogglePin}
              onStatusChange={handleStatusChange}
              onCategoryChange={handleCategoryChange}
              onSeverityChange={handleSeverityChange}
              onVisibilityChange={handleVisibilityChange}
              onAssigneeChange={handleAssigneeChange}
              assigneeOptions={assigneeOptions}
              onTodayToggle={handleTodayToggle}
              showTodayToggle={false}
              compact
            />
          ) : (
            // Phase 5: List view splits into TODAY + BACKLOG.
            // Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW
            //   lane inserted between TODAY and BACKLOG.
            // Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP
            //   lane inserted between TOMORROW and BACKLOG.
            //
            // Lane resolution (highest priority first):
            //   is_today=true                → TODAY
            //   tomorrow_set_at IS NOT NULL  → TOMORROW
            //   next_up_set_at IS NOT NULL   → NEXT UP   ← v37
            //   else                         → BACKLOG
            //
            // Backend enforces three-way mutual exclusion so an item
            // never appears in multiple lanes simultaneously. The
            // midnight-ET rollover cron promotes TOMORROW → TODAY;
            // NEXT UP is manual-only ("tickets here don't move unless
            // I move them").
            //
            // Each section header is collapsible (v37) — `collapsed`
            // state is per-browser via the `storage` service.
            (() => {
              const todayItems = items.filter((i) => i.is_today);
              const tomorrowItems = items.filter(
                (i) => !i.is_today && i.tomorrow_set_at != null
              );
              const nextUpItems = items.filter(
                (i) => !i.is_today
                    && i.tomorrow_set_at == null
                    && i.next_up_set_at != null
              );
              const backlogItems = items.filter(
                (i) => !i.is_today
                    && i.tomorrow_set_at == null
                    && i.next_up_set_at == null
              );
              const divider = (
                <div style={{
                  height: 0,
                  borderTop: `1px dashed ${T.limestone}`,
                  margin: '8px 0',
                }} />
              );
              return (
                <>
                  <SectionHeader
                    label="TODAY"
                    count={todayItems.length}
                    hint="Items the team is actively working on right now"
                    collapsed={collapsed.today}
                    onToggleCollapse={() => toggleCollapse('today')}
                  />
                  {!collapsed.today && (todayItems.length === 0 ? (
                    <EmptySection text="No items in focus today — toggle items into TODAY from BACKLOG below." />
                  ) : (
                    <RoadmapTable
                      items={todayItems}
                      total={todayItems.length}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onStatusChange={handleStatusChange}
                      onCategoryChange={handleCategoryChange}
                      onSeverityChange={handleSeverityChange}
                      onVisibilityChange={handleVisibilityChange}
                      onAssigneeChange={handleAssigneeChange}
                      assigneeOptions={assigneeOptions}
                      onTodayToggle={handleTodayToggle}
                      onTomorrowToggle={handleTomorrowToggle}
                      onNextUpToggle={handleNextUpToggle}
                      showTodayToggle
                      onReorder={(ids) => handleReorder('list', ids)}
                    />
                  ))}
                  {divider}
                  <SectionHeader
                    label="TOMORROW"
                    count={tomorrowItems.length}
                    hint="Plan tomorrow's focus now — the midnight cron rolls these into TODAY"
                    collapsed={collapsed.tomorrow}
                    onToggleCollapse={() => toggleCollapse('tomorrow')}
                  />
                  {!collapsed.tomorrow && (tomorrowItems.length === 0 ? (
                    <EmptySection text="No items planned for tomorrow — tap the moon next to a BACKLOG item to pin it." />
                  ) : (
                    <RoadmapTable
                      items={tomorrowItems}
                      total={tomorrowItems.length}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onStatusChange={handleStatusChange}
                      onCategoryChange={handleCategoryChange}
                      onSeverityChange={handleSeverityChange}
                      onVisibilityChange={handleVisibilityChange}
                      onAssigneeChange={handleAssigneeChange}
                      assigneeOptions={assigneeOptions}
                      onTodayToggle={handleTodayToggle}
                      onTomorrowToggle={handleTomorrowToggle}
                      onNextUpToggle={handleNextUpToggle}
                      showTodayToggle
                      onReorder={(ids) => handleReorder('list', ids)}
                    />
                  ))}
                  {divider}
                  <SectionHeader
                    label="NEXT UP"
                    count={nextUpItems.length}
                    hint="Queued for after tomorrow — does NOT auto-promote; move it manually when you're ready"
                    collapsed={collapsed.next_up}
                    onToggleCollapse={() => toggleCollapse('next_up')}
                  />
                  {!collapsed.next_up && (nextUpItems.length === 0 ? (
                    <EmptySection text="No items in NEXT UP — tap the queue button next to a BACKLOG item to pin it." />
                  ) : (
                    <RoadmapTable
                      items={nextUpItems}
                      total={nextUpItems.length}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onStatusChange={handleStatusChange}
                      onCategoryChange={handleCategoryChange}
                      onSeverityChange={handleSeverityChange}
                      onVisibilityChange={handleVisibilityChange}
                      onAssigneeChange={handleAssigneeChange}
                      assigneeOptions={assigneeOptions}
                      onTodayToggle={handleTodayToggle}
                      onTomorrowToggle={handleTomorrowToggle}
                      onNextUpToggle={handleNextUpToggle}
                      showTodayToggle
                      onReorder={(ids) => handleReorder('list', ids)}
                    />
                  ))}
                  {divider}
                  <SectionHeader
                    label="BACKLOG"
                    count={backlogItems.length}
                    hint="Everything else not yet shipped — pull into TODAY, TOMORROW, or NEXT UP when ready"
                    collapsed={collapsed.backlog}
                    onToggleCollapse={() => toggleCollapse('backlog')}
                  />
                  {!collapsed.backlog && (
                    <RoadmapTable
                      items={backlogItems}
                      total={backlogItems.length}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onStatusChange={handleStatusChange}
                      onCategoryChange={handleCategoryChange}
                      onSeverityChange={handleSeverityChange}
                      onVisibilityChange={handleVisibilityChange}
                      onAssigneeChange={handleAssigneeChange}
                      assigneeOptions={assigneeOptions}
                      onTodayToggle={handleTodayToggle}
                      onTomorrowToggle={handleTomorrowToggle}
                      onNextUpToggle={handleNextUpToggle}
                      showTodayToggle
                      onReorder={(ids) => handleReorder('list', ids)}
                    />
                  )}
                </>
              );
            })()
          )}
          {total > PAGE_SIZE && (
            <div style={{
              padding: '12px 18px',
              borderTop: `1px solid ${T.limestone}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontSize: 12, color: T.textSecondary,
            }}>
              <span>
                Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  style={paginationBtn(offset === 0)}
                >
                  Previous
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                  style={paginationBtn(offset + PAGE_SIZE >= total)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Editor modal ──────────────────────────────────────── */}
      {(creating || editingItem) && (
        <RoadmapEditorModal
          item={editingItem}
          linkedTickets={editingItem ? editingItemTickets : []}
          composition={composition}
          onClose={closeEditor}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

// ── Composition strip ──────────────────────────────────────────────

const CompositionStrip: React.FC<{ composition: AdminCompositionResponse }> = ({
  composition,
}) => {
  const pct = Math.round(composition.pct_new_or_improved_public * 100);
  const underTarget = pct < 80 && composition.public.total > 0;

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <StatCell
          label="Public items"
          value={composition.public.total}
          color={T.cedarPrimary}
        />
        <StatCell
          label="% new or improved"
          value={`${pct}%`}
          color={underTarget ? T.warning : T.cedarPrimary}
          warning={underTarget}
        />
        <StatCell
          label="Internal backlog"
          value={composition.internal.total}
          color={T.textSecondary}
        />
        <StatCell
          label="Known issues (public)"
          value={composition.public.by_category?.known_issue ?? 0}
          color={(composition.public.by_category?.known_issue ?? 0) > 5 ? T.warning : T.textSecondary}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{
          height: 8, background: T.limestone, borderRadius: 4, overflow: 'hidden',
          display: 'flex',
        }}>
          {composition.public.total > 0 && (
            <>
              <CategorySegment
                widthPct={((composition.public.by_category?.feature ?? 0) / composition.public.total) * 100}
                color="#1F6C4F"
                title="Features"
              />
              <CategorySegment
                widthPct={((composition.public.by_category?.improvement ?? 0) / composition.public.total) * 100}
                color="#42A5A5"
                title="Improvements"
              />
              <CategorySegment
                widthPct={((composition.public.by_category?.known_issue ?? 0) / composition.public.total) * 100}
                color="#F59E0B"
                title="Known issues"
              />
            </>
          )}
        </div>
        <p style={{ fontSize: 11, color: T.textTertiary, margin: '6px 0 0' }}>
          Public feed composition. Target: ≥80% new or improved (features + improvements).
        </p>
      </div>
    </div>
  );
};

const StatCell: React.FC<{
  label: string;
  value: number | string;
  color: string;
  warning?: boolean;
}> = ({ label, value, color, warning }) => (
  <div>
    <div style={{ ...sectionLabel, marginBottom: 4 }}>{label}</div>
    <div style={{
      fontSize: 24, fontWeight: 700, color,
      display: 'flex', alignItems: 'center', gap: 6,
      fontFamily: T.fontDisplay,
    }}>
      {value}
      {warning && <AlertTriangle size={18} />}
    </div>
  </div>
);

const CategorySegment: React.FC<{ widthPct: number; color: string; title: string }> = ({
  widthPct, color, title,
}) => {
  if (widthPct <= 0) return null;
  return (
    <div
      style={{ width: `${widthPct}%`, background: color, height: '100%' }}
      title={`${title}: ${widthPct.toFixed(1)}%`}
    />
  );
};

// ── Table ──────────────────────────────────────────────────────────

const RoadmapTable: React.FC<{
  items: AdminRoadmapItem[];
  total: number;
  onEdit: (item: AdminRoadmapItem) => void;
  onDelete: (item: AdminRoadmapItem) => void;
  onTogglePin: (item: AdminRoadmapItem) => void;
  // Phase 5: inline-PATCH handlers + visibility flag for the TODAY toggle
  onStatusChange?: (item: AdminRoadmapItem, newStatus: RoadmapStatus) => void;
  // Phase 4 polish (2026-05-11): inline category dropdown — mirrors status
  onCategoryChange?: (item: AdminRoadmapItem, newCategory: RoadmapCategory) => void;
  // Phase 4 polish v2 (2026-05-11): inline severity + visibility dropdowns
  onSeverityChange?: (item: AdminRoadmapItem, newSeverity: RoadmapSeverity | '') => void;
  onVisibilityChange?: (item: AdminRoadmapItem, newVisibility: RoadmapVisibility) => void;
  // Phase 6 polish v12 (2026-05-11): inline assignee dropdown
  onAssigneeChange?: (item: AdminRoadmapItem, newAssignee: string) => void;
  // Phase 6 polish v16 (2026-05-12): dynamic assignee options threaded
  // from the page (live admin handles from /mentions/handles).
  assigneeOptions?: { value: string; label: string }[];
  onTodayToggle?: (item: AdminRoadmapItem) => void;
  // Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW lane.
  onTomorrowToggle?: (item: AdminRoadmapItem) => void;
  // Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP lane.
  onNextUpToggle?: (item: AdminRoadmapItem) => void;
  showTodayToggle?: boolean;
  // Phase 6 polish v10 (2026-05-11): /completed view passes true to
  // collapse visibility/votes/sources columns + widen the title.
  compact?: boolean;
  // Migration 1731 — when provided, this lane's rows become drag-to-reorder
  // (display follows list_manual_rank; the drop persists the new order via
  // handleReorder('list', …)). Omit for read-only views (completed/declined).
  onReorder?: (orderedIds: string[]) => void;
}> = ({ items, onEdit, onDelete, onTogglePin, onStatusChange, onCategoryChange, onSeverityChange, onVisibilityChange, onAssigneeChange, assigneeOptions, onTodayToggle, onTomorrowToggle, onNextUpToggle, showTodayToggle, compact, onReorder }) => {
  // Sensors must be created unconditionally (React hooks rule). The custom
  // sensor won't start a drag from an in-row control (RowReorderPointerSensor's
  // interactive-target guard), so clicks + the inline <select>s keep working;
  // the 8px distance means a press-and-release on the row background is a no-op.
  const sensors = useSensors(
    useSensor(RowReorderPointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Within a lane, display order follows list_manual_rank; un-dragged rows
  // keep the server order (Infinity tail + stable sort).
  const ordered = onReorder
    ? [...items].sort(
        (a, b) => (a.list_manual_rank ?? Infinity) - (b.list_manual_rank ?? Infinity),
      )
    : items;

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = ordered.map((i) => i.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  const renderRow = (item: AdminRoadmapItem) => (
    <TableRow
      item={item}
      onEdit={onEdit}
      onDelete={onDelete}
      onTogglePin={onTogglePin}
      onStatusChange={onStatusChange}
      onCategoryChange={onCategoryChange}
      onSeverityChange={onSeverityChange}
      onVisibilityChange={onVisibilityChange}
      onAssigneeChange={onAssigneeChange}
      assigneeOptions={assigneeOptions}
      onTodayToggle={onTodayToggle}
      onTomorrowToggle={onTomorrowToggle}
      onNextUpToggle={onNextUpToggle}
      showTodayToggle={!!showTodayToggle}
      compact={!!compact}
    />
  );

  return (
    <div>
      <TableHeader showTodayToggle={!!showTodayToggle} compact={!!compact} />
      {onReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={ordered.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {ordered.map((item) => (
              <SortableTableRow key={item.id} id={item.id}>
                {renderRow(item)}
              </SortableTableRow>
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        ordered.map((item) => (
          <React.Fragment key={item.id}>{renderRow(item)}</React.Fragment>
        ))
      )}
    </div>
  );
};

// Whole-row drag-to-reorder wrapper. The drag listeners live on the row wrapper
// itself (no separate handle), so an admin grabs anywhere on a row and drags to
// reorder — matching the Kanban's whole-card behavior. A press on any in-row
// control never starts a drag (RowReorderPointerSensor's interactive-target
// guard), so a plain click still opens the editor (title button), toggles a
// lane, or opens an inline <select>. `cursor: grab` on the row background is the
// affordance; native controls keep their own pointer cursor. No hover-revealed
// grip — per Matt's "grab anywhere on a row, no tiny handle to hunt for."
const SortableTableRow: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const SectionHeader: React.FC<{
  label: string;
  count: number;
  hint?: string;
  // Phase 6 polish v37 (2026-06-01): collapsible sections. When
  // `onToggleCollapse` is provided, a chevron renders on the left
  // and the whole header becomes clickable. State persistence is
  // owned by the parent (storage-backed) — header just renders.
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}> = ({ label, count, hint, collapsed, onToggleCollapse }) => {
  const interactive = !!onToggleCollapse;
  return (
    <div
      onClick={interactive ? onToggleCollapse : undefined}
      role={interactive ? 'button' : undefined}
      aria-expanded={interactive ? !collapsed : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleCollapse?.();
        }
      } : undefined}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        padding: '14px 18px 8px',
        borderBottom: `1px solid ${T.limestone}`,
        background: '#FCFCFC',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {interactive && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            fontSize: 11,
            color: T.textTertiary,
            width: 12,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 120ms ease-out',
          }}
        >
          ▾
        </span>
      )}
      <span style={{
        fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: T.cedarPrimary,
      }}>
        {label}
      </span>
      <span style={{ fontSize: 12, color: T.textTertiary, fontVariantNumeric: 'tabular-nums' }}>
        {count} item{count === 1 ? '' : 's'}
      </span>
      {hint && (
        <span style={{ fontSize: 12, color: T.textTertiary, marginLeft: 'auto' }}>
          {hint}
        </span>
      )}
    </div>
  );
};

const EmptySection: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    padding: '24px 18px',
    textAlign: 'center',
    fontSize: 13,
    color: T.textTertiary,
    fontStyle: 'italic',
    borderBottom: `1px solid ${T.limestone}`,
  }}>
    {text}
  </div>
);

// Phase 5: GRID_COLS now has an optional 50px first column for the
// TODAY toggle. Without the toggle (e.g. /completed view), the first
// column is omitted.
//
// Phase 4 polish (2026-05-11): added a SEVERITY column between CATEGORY
// and STATUS.
//
// Phase 4 polish v5 (2026-05-11): severity column slimmed from
// minmax(100px, 0.8fr) → 40px. The cell is now just a colored circle
// (no text), so it doesn't need the wider track. Reclaiming ~60px
// lets the title column (2fr) breathe.
//
// Phase 6 polish v12 (2026-05-11): Votes + Sources columns DROPPED
// from the list view per user feedback — those numbers stay
// available inside the editor modal, just not in the row at a
// glance. Frees ~100px of horizontal space for the new Assignee
// column + wider title.
//
// Columns now (full mode, 10 with today / 9 without):
//   today? · title · category · sev · status · visibility · assignee · updated · actions
// Columns (/completed compact, 6):
//   title · category · sev · status · updated · actions
//   (also no assignee — shipped items don't need ownership tracking)
// Phase 6 polish v24 (2026-05-13, migration 1495): a second 40px toggle
// column (TOMORROW moon) sits next to the existing TODAY checkmark so
// admins can pin items to either lane without leaving the row.
// Phase 6 polish v37 (2026-06-01, migration 1598): a third 40px toggle
// column (NEXT UP ⏫) sits next to TOMORROW. Total lane-toggle width
// is now 50 + 40 + 40 = 130px on the left of every row.
const GRID_COLS_WITH_TODAY =
  '50px 40px 40px minmax(260px, 2.4fr) minmax(100px, 0.8fr) 40px minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) 88px 56px';
const GRID_COLS_NO_TODAY =
  'minmax(260px, 2.4fr) minmax(100px, 0.8fr) 40px minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) 88px 56px';
const GRID_COLS_COMPACT =
  'minmax(400px, 3fr) minmax(100px, 0.8fr) 40px minmax(100px, 0.8fr) 88px 56px';

const TableHeader: React.FC<{ showTodayToggle: boolean; compact?: boolean }> = ({ showTodayToggle, compact }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: compact
      ? GRID_COLS_COMPACT
      : (showTodayToggle ? GRID_COLS_WITH_TODAY : GRID_COLS_NO_TODAY),
    gap: 12,
    padding: '10px 18px',
    borderBottom: `1px solid ${T.limestone}`,
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.04em', color: T.textTertiary,
    background: '#FCFCFC',
  }}>
    {!compact && showTodayToggle && <span style={{ textAlign: 'center' }}>Today</span>}
    {/* Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW toggle
        column header sits between TODAY and TITLE so admins can pin
        items to either lane inline. */}
    {!compact && showTodayToggle && <span style={{ textAlign: 'center' }}>Tmrw</span>}
    {/* Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP toggle
        column. Manual-only queue lane (does not auto-promote). */}
    {!compact && showTodayToggle && <span style={{ textAlign: 'center' }}>Next</span>}
    <span>Title</span>
    <span>Category</span>
    {/* Phase 6 polish v6 (2026-05-11): the severity column is 40px
        narrow + just a colored circle. Center the header label over
        the circle and clip via overflow:hidden so "Severity" doesn't
        spill onto the next column. Title attribute keeps the full
        label discoverable on hover. */}
    <span
      title="Severity"
      style={{
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
      }}
    >
      Sev
    </span>
    <span>Status</span>
    {/* Phase 6 polish v10 (2026-05-11): /completed compact view drops
        Visibility and Assignee — shipped items don't need them. */}
    {!compact && <span>Visibility</span>}
    {/* Phase 6 polish v12 (2026-05-11): Assignee column replaces the
        old Votes + Sources slots so admins see who owns what at a
        glance. Votes + Sources still surface inside the editor modal. */}
    {!compact && <span>Assignee</span>}
    <span>Updated</span>
    <span style={{ textAlign: 'right' }}>Actions</span>
  </div>
);

const TableRow: React.FC<{
  item: AdminRoadmapItem;
  onEdit: (item: AdminRoadmapItem) => void;
  onDelete: (item: AdminRoadmapItem) => void;
  onTogglePin: (item: AdminRoadmapItem) => void;
  onStatusChange?: (item: AdminRoadmapItem, newStatus: RoadmapStatus) => void;
  // Phase 4 polish (2026-05-11): inline category dropdown
  onCategoryChange?: (item: AdminRoadmapItem, newCategory: RoadmapCategory) => void;
  // Phase 4 polish v2 (2026-05-11): inline severity + visibility dropdowns
  onSeverityChange?: (item: AdminRoadmapItem, newSeverity: RoadmapSeverity | '') => void;
  onVisibilityChange?: (item: AdminRoadmapItem, newVisibility: RoadmapVisibility) => void;
  // Phase 6 polish v12 (2026-05-11): inline assignee dropdown
  onAssigneeChange?: (item: AdminRoadmapItem, newAssignee: string) => void;
  // Phase 6 polish v16 (2026-05-12): assignee options threaded from the
  // page so the dropdown shows the live set of admin handles (not a
  // hardcoded matt/paul list). Falls back inside InlineAssigneeSelect
  // when undefined.
  assigneeOptions?: { value: string; label: string }[];
  onTodayToggle?: (item: AdminRoadmapItem) => void;
  // Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW lane.
  onTomorrowToggle?: (item: AdminRoadmapItem) => void;
  // Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP lane.
  onNextUpToggle?: (item: AdminRoadmapItem) => void;
  showTodayToggle?: boolean;
  // Phase 6 polish v10 (2026-05-11)
  compact?: boolean;
}> = ({ item, onEdit, onDelete, onTogglePin, onStatusChange, onCategoryChange, onSeverityChange, onVisibilityChange, onAssigneeChange, assigneeOptions, onTodayToggle, onTomorrowToggle, onNextUpToggle, showTodayToggle, compact }) => {
  const updatedAgo = useMemo(() => formatRelative(item.updated_at), [item.updated_at]);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: compact
          ? GRID_COLS_COMPACT
          : (showTodayToggle ? GRID_COLS_WITH_TODAY : GRID_COLS_NO_TODAY),
        gap: 12,
        padding: '12px 18px',
        borderBottom: `1px solid ${T.limestone}`,
        alignItems: 'center',
        fontSize: 13,
      }}
    >
      {/* Phase 5: inline TODAY toggle. Click to flip is_today via PATCH.
          Compact mode (/completed) skips the toggle entirely — shipped
          items don't need a "move to TODAY" action. */}
      {!compact && showTodayToggle && (
        <button
          onClick={() => onTodayToggle?.(item)}
          title={item.is_today ? "Remove from TODAY" : "Move to TODAY"}
          aria-label={item.is_today ? "Remove from TODAY" : "Move to TODAY"}
          aria-pressed={!!item.is_today}
          style={{
            width: 22, height: 22,
            borderRadius: '50%',
            border: `2px solid ${item.is_today ? T.cedarPrimary : T.limestone}`,
            background: item.is_today ? T.cedarPrimary : '#FFFFFF',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto',
            padding: 0,
          }}
        >
          {item.is_today && <Check size={12} color="#FFFFFF" />}
        </button>
      )}
      {/* Phase 6 polish v24 (2026-05-13, migration 1495): TOMORROW toggle.
          Sibling to the TODAY circle. Moon icon = "this is tomorrow's
          plan" (set-it-and-forget-it overnight). Backend enforces
          mutual exclusion with is_today. Hidden in compact mode for
          the same reason as TODAY — shipped items don't get planned. */}
      {!compact && showTodayToggle && (
        <button
          onClick={() => onTomorrowToggle?.(item)}
          title={item.tomorrow_set_at ? "Remove from TOMORROW" : "Pin for TOMORROW"}
          aria-label={item.tomorrow_set_at ? "Remove from TOMORROW" : "Pin for TOMORROW"}
          aria-pressed={!!item.tomorrow_set_at}
          style={{
            width: 22, height: 22,
            borderRadius: '50%',
            border: `2px solid ${item.tomorrow_set_at ? '#6366F1' : T.limestone}`,
            background: item.tomorrow_set_at ? '#6366F1' : '#FFFFFF',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto',
            padding: 0,
          }}
        >
          <Moon
            size={11}
            color={item.tomorrow_set_at ? '#FFFFFF' : '#9CA3AF'}
            fill={item.tomorrow_set_at ? '#FFFFFF' : 'none'}
          />
        </button>
      )}
      {/* Phase 6 polish v37 (2026-06-01, migration 1598): NEXT UP toggle.
          Sibling to the TODAY + TOMORROW circles. ChevronsUp icon =
          "manual queue — promote later when ready." Backend enforces
          three-way mutual exclusion with is_today + is_tomorrow.
          Phase 6 polish v38 (2026-06-01): swapped ⏫ emoji → ChevronsUp
          lucide icon per the project's no-emoji-icons rule. */}
      {!compact && showTodayToggle && (
        <button
          onClick={() => onNextUpToggle?.(item)}
          title={item.next_up_set_at ? "Remove from NEXT UP" : "Pin for NEXT UP"}
          aria-label={item.next_up_set_at ? "Remove from NEXT UP" : "Pin for NEXT UP"}
          aria-pressed={!!item.next_up_set_at}
          style={{
            width: 22, height: 22,
            borderRadius: '50%',
            border: `2px solid ${item.next_up_set_at ? '#0EA5E9' : T.limestone}`,
            background: item.next_up_set_at ? '#0EA5E9' : '#FFFFFF',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto',
            padding: 0,
          }}
        >
          <ChevronsUp
            size={12}
            color={item.next_up_set_at ? '#FFFFFF' : '#9CA3AF'}
          />
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {item.pinned && (
          <Pin
            size={12}
            color={T.cedarPrimary}
            style={{ flexShrink: 0 }}
          />
        )}
        {/* Phase 6 polish v30 (2026-05-15): needs_triage `?` marker
            removed from the list view. The data still flows (backend
            column + ingester classifier + v29 auto-clear still run),
            but admins don't need a visible "not-yet-classified" pip —
            the absence of a category/severity already tells them. */}
        <button
          onClick={() => onEdit(item)}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontFamily: T.fontBody, fontSize: 13, fontWeight: 500,
            color: T.textPrimary, textAlign: 'left',
            cursor: 'pointer',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            minWidth: 0,
          }}
          title={item.title}
        >
          {item.title}
        </button>
      </div>
      {onCategoryChange ? (
        <InlineCategorySelect item={item} onChange={(c) => onCategoryChange(item, c)} />
      ) : (
        <CatBadge cat={item.category} />
      )}
      {/* Phase 6 polish v6 (2026-05-11): center the circle within its
          slim 40px column so it lines up under the "Sev" header. */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {onSeverityChange ? (
          <InlineSeveritySelect item={item} onChange={(s) => onSeverityChange(item, s)} />
        ) : (
          <SeverityBadge severity={(item.severity as RoadmapSeverity | null) ?? null} />
        )}
      </div>
      {onStatusChange ? (
        <InlineStatusSelect item={item} onChange={(s) => onStatusChange(item, s)} />
      ) : (
        <StatusBadgeCell status={item.status} />
      )}
      {/* Phase 6 polish v10 (2026-05-11): /completed compact view
          drops Visibility cell. */}
      {!compact && (
        onVisibilityChange ? (
          <InlineVisibilitySelect item={item} onChange={(v) => onVisibilityChange(item, v)} />
        ) : (
          <VisBadge vis={item.visibility} />
        )
      )}
      {/* Phase 6 polish v12 (2026-05-11): Assignee column. Votes +
          Sources cells dropped (still visible in the editor modal).
          Renders an inline dropdown (Matt / Paul / Unassigned) so
          admins can claim or reassign work without opening the modal. */}
      {!compact && (
        onAssigneeChange ? (
          <InlineAssigneeSelect
            item={item}
            options={assigneeOptions}
            onChange={(a) => onAssigneeChange(item, a)}
          />
        ) : (
          <AssigneeBadge assignee={item.assignee ?? null} />
        )
      )}
      <span style={{ color: T.textSecondary, fontSize: 12 }}>{updatedAgo}</span>
      {/* Phase 6 polish v10 (2026-05-11): Edit pencil dropped from
          the action column — the title cell is already a clickable
          <button> that opens the editor modal, so the pencil was
          redundant. Pin + Delete remain. */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <IconActionButton
          title={item.pinned ? 'Unpin' : 'Pin'}
          onClick={() => onTogglePin(item)}
        >
          {item.pinned ? <PinOff size={14} /> : <Pin size={14} />}
        </IconActionButton>
        <IconActionButton
          title="Delete"
          onClick={() => onDelete(item)}
          danger
        >
          <Trash2 size={14} />
        </IconActionButton>
      </div>
    </div>
  );
};

// Phase 6 polish v11 (2026-05-11): category pills are OUTLINED to be
// visually distinct from filled status pills, even when the same hue
// family appears in both (e.g. Bug-red category + Declined-red status).
const CatBadge: React.FC<{ cat: RoadmapCategory }> = ({ cat }) => {
  const c = CATEGORY_COLORS[cat];
  return (
    <span style={{
      ...badge(c.bg, c.fg),
      border: `1px solid ${c.fg}`,
    }}>
      {CATEGORY_LABEL[cat]}
    </span>
  );
};
const StatusBadgeCell: React.FC<{ status: RoadmapStatus }> = ({ status }) => {
  const c = STATUS_COLORS[status];
  return <span style={badge(c.bg, c.fg)}>{STATUS_LABEL[status]}</span>;
};
const VisBadge: React.FC<{ vis: RoadmapVisibility }> = ({ vis }) => {
  const c = VISIBILITY_COLORS[vis];
  return (
    <span style={{ ...badge(c.bg, c.fg), gap: 4 }}>
      {vis === 'public' ? <Eye size={11} /> : <EyeOff size={11} />}
      {VISIBILITY_LABEL[vis]}
    </span>
  );
};

// Phase 4 polish v5 (2026-05-11): severity column is a colored circle
// only — no text. User feedback: "we just need to show a colour, we
// do not need the text like important/ review etc."
//
// Phase 4 polish v7 (2026-05-11): NULL severity renders as a solid
// BLUE circle (FEATURE_STRIPE_COLOR), mirroring the 🔵 Slack-dot
// convention for features. Previously was a muted dashed-grey ring —
// user feedback: "none should be feature = blue".
//
// Color uses the SEVERITY_COLORS palette's stripe value for bugs
// (saturated red/amber/green matching the Kanban left-stripe
// convention) and FEATURE_STRIPE_COLOR for features. Title tooltip
// surfaces the meaning on hover for a11y / discoverability.
const SEVERITY_CIRCLE_SIZE = 16;
const SeverityBadge: React.FC<{ severity: RoadmapSeverity | null }> = ({ severity }) => {
  if (!severity) {
    return (
      <span
        title="Feature (no severity)"
        style={{
          display: 'inline-block',
          width: SEVERITY_CIRCLE_SIZE,
          height: SEVERITY_CIRCLE_SIZE,
          borderRadius: '50%',
          background: FEATURE_STRIPE_COLOR,
        }}
      />
    );
  }
  const c = SEVERITY_COLORS[severity];
  return (
    <span
      title={`Severity: ${severity}`}
      style={{
        display: 'inline-block',
        width: SEVERITY_CIRCLE_SIZE,
        height: SEVERITY_CIRCLE_SIZE,
        borderRadius: '50%',
        background: c.stripe,
      }}
    />
  );
};

// Phase 4 polish (2026-05-11): inline category dropdown per row.
// Mirrors InlineStatusSelect. Onchange fires PATCH so admins can
// reclassify items (e.g. flip a misclassified Slack ingest from
// 'feature' to 'known_issue') without opening the editor modal.
const InlineCategorySelect: React.FC<{
  item: AdminRoadmapItem;
  onChange: (newCategory: RoadmapCategory) => void;
}> = ({ item, onChange }) => {
  const c = CATEGORY_COLORS[item.category];
  return (
    <select
      value={item.category}
      onChange={(e) => onChange(e.target.value as RoadmapCategory)}
      style={{
        ...badge(c.bg, c.fg),
        // Phase 6 polish v11: category pills are OUTLINED for visual
        // distinction from filled status pills (same hue families
        // can otherwise blur — e.g. Bug-red category vs Declined-red
        // status).
        border: `1px solid ${c.fg}`,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 18,
        background: c.bg,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 4l3 3 3-3' stroke='${encodeURIComponent(c.fg)}' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 6px center',
      }}
    >
      <option value="feature">{CATEGORY_LABEL.feature}</option>
      <option value="improvement">{CATEGORY_LABEL.improvement}</option>
      <option value="known_issue">{CATEGORY_LABEL.known_issue}</option>
    </select>
  );
};

// Phase 4 polish v5 (2026-05-11): inline severity dropdown rendered as
// a colored circle (no text in the closed state). Same UX as
// SeverityBadge — user clicks the circle → native browser dropdown
// opens with the four labeled options (Feature / Critical /
// Important / Review). The closed-state text is hidden via
// `color: transparent` so all you see is the circle.
//
// Phase 4 polish v7 (2026-05-11): NULL severity renders as a solid
// blue circle (FEATURE_STRIPE_COLOR) — mirrors the 🔵 Slack dot for
// features. The empty option label is now "🔵 Feature" instead of
// "— None" per user feedback.
const InlineSeveritySelect: React.FC<{
  item: AdminRoadmapItem;
  onChange: (newSeverity: RoadmapSeverity | '') => void;
}> = ({ item, onChange }) => {
  const sev = (item.severity as RoadmapSeverity | null) ?? null;
  const stripe = sev ? SEVERITY_COLORS[sev].stripe : FEATURE_STRIPE_COLOR;
  return (
    <select
      value={sev ?? ''}
      onChange={(e) => onChange(e.target.value as RoadmapSeverity | '')}
      title={sev ? `Severity: ${sev}` : 'Feature (no severity) — click to change'}
      style={{
        // Sized to match the SeverityBadge circle. text-indent +
        // transparent color hide the displayed option text in the
        // closed state; the native dropdown menu still shows option
        // labels with their original color (option text inherits its
        // own color, not the select's).
        width: SEVERITY_CIRCLE_SIZE + 4,
        height: SEVERITY_CIRCLE_SIZE + 4,
        borderRadius: '50%',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        background: stripe,
        border: 'none',
        cursor: 'pointer',
        color: 'transparent',
        textIndent: -9999,
        padding: 0,
        fontSize: 0,
        // No chevron — the colored circle alone signals "clickable".
        // Browsers won't render the default dropdown indicator with
        // appearance: none.
      }}
    >
      {/* Option styling overrides the parent's transparent color so
          the dropdown menu (when opened) shows the full text labels.
          🔵 = feature (no severity); 🔴/🟡/🟢 = bug severities. */}
      <option value="" style={{ color: '#000', background: '#fff' }}>🔵 Feature</option>
      <option value="critical" style={{ color: '#000', background: '#fff' }}>🔴 {SEVERITY_LABEL.critical}</option>
      <option value="important" style={{ color: '#000', background: '#fff' }}>🟡 {SEVERITY_LABEL.important}</option>
      <option value="review" style={{ color: '#000', background: '#fff' }}>🟢 {SEVERITY_LABEL.review}</option>
    </select>
  );
};

// Phase 4 polish v2 (2026-05-11): inline visibility dropdown per row.
// Mirrors InlineCategorySelect. Visibility flips can shift the public
// composition meter (e.g. promoting an internal-only item to public
// changes the new/improved %); the parent handler reloads composition.
const InlineVisibilitySelect: React.FC<{
  item: AdminRoadmapItem;
  onChange: (newVisibility: RoadmapVisibility) => void;
}> = ({ item, onChange }) => {
  const c = VISIBILITY_COLORS[item.visibility];
  return (
    <select
      value={item.visibility}
      onChange={(e) => onChange(e.target.value as RoadmapVisibility)}
      style={{
        ...badge(c.bg, c.fg),
        border: 'none',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 18,
        background: c.bg,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 4l3 3 3-3' stroke='${encodeURIComponent(c.fg)}' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 6px center',
      }}
    >
      <option value="public">{VISIBILITY_LABEL.public}</option>
      <option value="internal_only">{VISIBILITY_LABEL.internal_only}</option>
      <option value="hidden">{VISIBILITY_LABEL.hidden}</option>
    </select>
  );
};

// Phase 6 polish v12 (2026-05-11): assignee = the slack_handle of the
// admin who owns this item (or NULL = unassigned). Two pieces:
//
//   AssigneeBadge      — read-only display (used when no callback wired)
//   InlineAssigneeSelect — clickable dropdown (Matt / Paul / Unassigned)
//
// Capitalize the handle for display (matt → Matt). Unassigned renders
// as a muted dash to match the SeverityBadge convention.
//
// Phase 6 polish v16 (2026-05-12): assignee options are now built from
// the /mentions/handles endpoint (admin_users WHERE slack_handle IS NOT
// NULL) so adding a new admin doesn't require a code change. The
// fallback (matt + paul) keeps the dropdown usable if the fetch fails
// or hasn't completed yet — same two handles the migration 1456 seed
// guarantees exist.
const FALLBACK_ASSIGNEE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Unassigned' },
  { value: 'matt', label: 'Matt' },
  { value: 'paul', label: 'Paul' },
];

const buildAssigneeOptions = (
  handles: MentionHandle[] | null,
): { value: string; label: string }[] => {
  if (!handles || handles.length === 0) return FALLBACK_ASSIGNEE_OPTIONS;
  return [
    { value: '', label: 'Unassigned' },
    ...handles.map((h) => ({
      value: h.handle,
      // Prefer the display_name (admin's full_name) when available;
      // fall back to capitalised handle. Matches the @mention
      // autocomplete's vocabulary so admins see the same names.
      label:
        h.display_name && h.display_name.trim().length > 0
          ? h.display_name
          : h.handle.charAt(0).toUpperCase() + h.handle.slice(1),
    })),
  ];
};

const _formatAssignee = (handle: string | null): string => {
  if (!handle) return '—';
  // Capitalize first letter for display
  return handle.charAt(0).toUpperCase() + handle.slice(1);
};

const AssigneeBadge: React.FC<{ assignee: string | null }> = ({ assignee }) => {
  if (!assignee) {
    return <span style={{ color: T.textTertiary, fontSize: 13 }}>—</span>;
  }
  return (
    <span style={badge('#F1F5F9', '#475569')}>
      {_formatAssignee(assignee)}
    </span>
  );
};

const InlineAssigneeSelect: React.FC<{
  item: AdminRoadmapItem;
  onChange: (newAssignee: string) => void;
  // Phase 6 polish v16 (2026-05-12): options threaded from the page so
  // the dropdown reflects the live admin set. Defaults to the matt/paul
  // fallback when undefined (e.g., before the handles fetch resolves).
  options?: { value: string; label: string }[];
}> = ({ item, onChange, options }) => {
  const current = item.assignee ?? '';
  const isUnassigned = current === '';
  const bg = isUnassigned ? 'transparent' : '#F1F5F9';
  const fg = isUnassigned ? T.textTertiary : '#475569';
  const effectiveOptions = options ?? FALLBACK_ASSIGNEE_OPTIONS;
  // Defensive: if the current assignee isn't in the option set (e.g.,
  // an admin's handle was removed from admin_users), surface it as a
  // standalone option so the dropdown doesn't silently misrepresent
  // the row's value. Admin can re-pick from the new set.
  const hasCurrent = current === '' || effectiveOptions.some((o) => o.value === current);
  const renderedOptions = hasCurrent
    ? effectiveOptions
    : [...effectiveOptions, { value: current, label: _formatAssignee(current) }];
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      title={isUnassigned ? 'Unassigned — click to assign' : `Owner: ${_formatAssignee(current)}`}
      style={{
        ...badge(bg, fg),
        border: isUnassigned ? `1px dashed ${T.textTertiary}` : 'none',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 18,
        background: bg,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 4l3 3 3-3' stroke='${encodeURIComponent(fg)}' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 6px center',
      }}
    >
      {renderedOptions.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

// Phase 5: inline status dropdown per row. Visually styled like a pill
// but clickable. Onchange fires PATCH directly via the parent's handler.
// `todo` is intentionally not in the option list (UI-deprecated per
// Phase 1.6). Existing rows at status=todo will still render the dropdown
// with current value displayed; admin can transition them out via this control.
const InlineStatusSelect: React.FC<{
  item: AdminRoadmapItem;
  onChange: (newStatus: RoadmapStatus) => void;
}> = ({ item, onChange }) => {
  const c = STATUS_COLORS[item.status];
  return (
    <select
      value={item.status}
      onChange={(e) => onChange(e.target.value as RoadmapStatus)}
      style={{
        ...badge(c.bg, c.fg),
        border: 'none',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 18,
        background: c.bg,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 4l3 3 3-3' stroke='${encodeURIComponent(c.fg)}' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 6px center',
      }}
    >
      {/* Phase 6 polish v6 (2026-05-11): 'duplicate' removed from the
          dropdown — still a valid DB value (set programmatically by
          the merge endpoint) but not manually pickable. 'stage' added
          for the staging-environment workflow step. Order roughly
          reflects forward progression. */}
      <option value="intake">Intake</option>
      <option value="under_review">Under Review</option>
      <option value="planned">Planned</option>
      <option value="in_progress">In Progress</option>
      <option value="stage">Stage</option>
      <option value="qa">QA</option>
      <option value="shipped">Shipped</option>
      <option value="declined">Declined</option>
      {/* `todo` is deprecated UI-side per Phase 1.6 but rendered as the
          current value if a legacy row exists at this status — admin
          uses the dropdown to transition out. Same treatment for
          `duplicate` — legacy/merge-set rows render their current
          value so admin can move them off. */}
      {item.status === 'todo' && <option value="todo">To Do (deprecated)</option>}
      {item.status === 'duplicate' && <option value="duplicate">Duplicate (merged)</option>}
    </select>
  );
};

const IconActionButton: React.FC<{
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, danger, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      width: 28, height: 28,
      borderRadius: T.radiusSm,
      background: danger ? '#FEF2F2' : T.limestone,
      border: 'none',
      color: danger ? '#991B1B' : T.textSecondary,
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {children}
  </button>
);

// ── Local helpers ──────────────────────────────────────────────────

const selectStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${T.limestone}`,
  borderRadius: T.radiusMd,
  background: '#FFFFFF',
  fontSize: 13, color: T.textPrimary,
  minWidth: 140, outline: 'none',
  fontFamily: T.fontBody,
};

const paginationBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  borderRadius: T.radiusSm,
  background: disabled ? '#F3F4F6' : '#FFFFFF',
  border: `1px solid ${T.limestone}`,
  fontSize: 12, fontWeight: 600,
  color: disabled ? T.textTertiary : T.cedarPrimary,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default AdminRoadmap;
